import React, { useRef, useState } from 'react';
import { ChevronLeft, ImageIcon } from 'lucide-react';
import Button from '../ui/Button';
import ImageUploader from '../ui/ImageUploader';
import { useFinanceForm } from '../../hooks/useFinanceForm';
import { useToast } from '../../hooks/useToast';

/**
 * 新增财务记录表单
 */
const FinanceFormCreate = ({ onBack, onSave, isSaving = false }) => {
  const toast = useToast();
  const {
    students,
    courses,
    selectedStudentId,
    selectedCourseId,
    paymentMethods,
    loading,
    getTodayDate,
    handleStudentChange,
    handleCourseChange,
    defaultValues,
    settingsLoaded
  } = useFinanceForm();

  // 图片上传状态
  const [paymentProofUrls, setPaymentProofUrls] = useState([]);

  // 推荐赠送状态
  const [referrerId, setReferrerId] = useState('');
  const [expectedReward, setExpectedReward] = useState('');

  // 达标赠送状态
  const [enableVolumeBonus, setEnableVolumeBonus] = useState(false);
  const [volumeBonusType, setVolumeBonusType] = useState('');
  const [volumeBonusHours, setVolumeBonusHours] = useState('');

  // 辅助函数：从赠送规则字符串中提取课时数 (例如 "满30送2节" -> 2)
  const extractHoursFromType = (typeStr) => {
    if (!typeStr) return '';
    const match = typeStr.match(/送(\d+(\.\d+)?)节/);
    return match ? match[1] : '';
  };

  // 自动同步赠送规则和赠送课时数
  React.useEffect(() => {
    if (enableVolumeBonus && volumeBonusType) {
      const extracted = extractHoursFromType(volumeBonusType);
      if (extracted) {
        setVolumeBonusHours(extracted);
      }
    }
  }, [volumeBonusType, enableVolumeBonus]);

  // 监听默认值变化并应用
  React.useEffect(() => {
    if (settingsLoaded && defaultValues) {
      if (unitPriceRef.current) {
        unitPriceRef.current.value = defaultValues.unit_price;
      }
      if (totalHoursRef.current) {
        totalHoursRef.current.value = defaultValues.total_hours;
        calculateAmount(); // 自动计算实付金额
      }
      setVolumeBonusType(defaultValues.bonus_type);
      setVolumeBonusHours(defaultValues.bonus_hours);
      setExpectedReward(defaultValues.referral_reward);
    }
  }, [settingsLoaded, defaultValues]);

  // 表单字段 refs
  const dateRef = useRef(null);
  const studentIdRef = useRef(null);
  const courseIdRef = useRef(null);
  const unitPriceRef = useRef(null);
  const totalHoursRef = useRef(null);
  const amountPaidRef = useRef(null);
  const paymentMethodRef = useRef(null);
  const remarksRef = useRef(null);

  // 自动计算实付金额
  const calculateAmount = () => {
    const unitPrice = parseFloat(unitPriceRef.current?.value) || 0;
    const totalHours = parseFloat(totalHoursRef.current?.value) || 0;
    const amount = unitPrice * totalHours;
    const roundedAmount = Math.round(amount); // 四舍五入取整数
    if (amountPaidRef.current) {
      amountPaidRef.current.value = roundedAmount;
    }
  };

  const handleSave = () => {
    // 注意：防重入锁由父组件 App.jsx 的 isSavingRef 统一管理，此处不重复判断
    // 收集表单数据
    const formData = {
      purchase_date: dateRef.current?.value,
      student_id: parseInt(studentIdRef.current?.value),
      course_id: parseInt(courseIdRef.current?.value),
      unit_price: parseFloat(unitPriceRef.current?.value) || 0,
      total_hours: parseFloat(totalHoursRef.current?.value) || 0,
      amount_paid: parseInt(amountPaidRef.current?.value) || 0,
      payment_method: paymentMethodRef.current?.value,
      remarks: remarksRef.current?.value || '',
      payment_proof_urls: paymentProofUrls,  // 支付凭证URL
      // 推荐赠送
      referrer_id: referrerId ? parseInt(referrerId) : null,
      expected_reward: referrerId ? parseFloat(expectedReward) : 0,
      // 达标赠送
      volume_bonus_type: enableVolumeBonus ? volumeBonusType : null,
      volume_bonus_hours: enableVolumeBonus ? parseFloat(volumeBonusHours) : 0,
    };

    // 基本验证
    if (!formData.purchase_date) {
      toast.error('请选择购买日期');
      return;
    }
    if (!formData.student_id) {
      toast.error('请选择学生');
      return;
    }
    if (!formData.course_id) {
      toast.error('请选择课程');
      return;
    }
    if (formData.payment_method !== '赠送' && (!formData.amount_paid || formData.amount_paid <= 0)) {
      toast.error('请输入有效的实付金额');
      return;
    }
    if (!formData.payment_method) {
      toast.error('请选择支付方式');
      return;
    }
    if (!formData.total_hours || formData.total_hours <= 0) {
      toast.error('请输入有效的总课时');
      return;
    }

    onSave(formData);
  };

  return (
    <div className="flex flex-col h-full bg-[#F7FAFC]">
      <header className="h-[56px] px-4 bg-white flex items-center justify-between border-b border-[#EDF2F7] flex-shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1 -ml-1 text-[#2D3748]">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-[#2D3748]">账务新增</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
        {/* 基本信息 */}
        <div className="bg-white rounded-xl border border-[#EDF2F7] overflow-hidden">
          {/* 购买日期 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              购买日期
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={dateRef}
              type="date" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]" 
              defaultValue={getTodayDate()}
              required
            />
          </div>

          {/* 学生姓名 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              学生姓名
              <span className="text-red-500">*</span>
            </label>
            <select 
              ref={studentIdRef}
              className="flex-1 text-sm bg-transparent outline-none text-[#2D3748] dir-rtl text-right"
              value={selectedStudentId}
              onChange={(e) => handleStudentChange(e.target.value)}
              required
            >
              <option value="">请选择学生</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* 课程名称 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              课程名称
              <span className="text-red-500">*</span>
            </label>
            <select 
              ref={courseIdRef}
              className="flex-1 text-sm bg-transparent outline-none text-[#2D3748] dir-rtl text-right disabled:text-gray-400"
              value={selectedCourseId}
              onChange={(e) => handleCourseChange(e.target.value)}
              disabled={loading}
              required
            >
              <option value="">{loading ? '加载中...' : '请选择课程'}</option>
              {courses.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* 课时单价 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              课时单价
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={unitPriceRef}
              type="number" 
              placeholder="0" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]" 
              min="0"
              step="0.01"
              onChange={calculateAmount}
              required
            />
          </div>

          {/* 总课时 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              总课时
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={totalHoursRef}
              type="number" 
              placeholder="0" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]" 
              min="0"
              step="0.1"
              onChange={calculateAmount}
              required
            />
          </div>

          {/* 实付金额 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              实付金额
            </label>
            <input 
              ref={amountPaidRef}
              type="number" 
              placeholder="0" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748] bg-gray-50" 
              min="0"
              step="1"
              readOnly
            />
          </div>

          {/* 支付方式 */}
          <div className="flex items-center px-4 py-3">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              支付方式
              <span className="text-red-500">*</span>
            </label>
            <select 
              ref={paymentMethodRef}
              className="flex-1 text-sm bg-transparent outline-none text-[#2D3748] dir-rtl text-right"
              required
            >
              <option value="">请选择</option>
              {paymentMethods.map(method => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
          </div>

          {/* 推荐人与预期赠送 */}
          <div className="flex flex-col border-t border-[#EDF2F7]">
            <div className="flex items-center px-4 py-3">
              <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
                推荐人
              </label>
              <select 
                className="flex-1 text-sm bg-transparent outline-none text-[#2D3748] dir-rtl text-right"
                value={referrerId}
                onChange={(e) => setReferrerId(e.target.value)}
              >
                <option value="">无</option>
                {students.filter(s => s.id !== parseInt(selectedStudentId)).map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            {/* 推荐政策描述 - 小字提示 */}
            <div className="px-4 pb-2 -mt-1">
              <p className="text-[10px] text-gray-400 leading-tight">
                当前政策: 被推荐人完课 {defaultValues.referral_milestone || '0'} 节后自动发放。
              </p>
            </div>
          </div>
          {referrerId && (
            <div className="flex items-center px-4 py-3 border-t border-[#EDF2F7] bg-green-50">
              <label className="w-24 text-sm font-medium text-green-700 flex items-center gap-1">
                双向推荐赠送
              </label>
              <input 
                type="number" 
                className="flex-1 text-sm text-right bg-transparent outline-none text-green-700 font-medium" 
                min="0"
                step="0.1"
                value={expectedReward}
                onChange={(e) => setExpectedReward(e.target.value)}
              />
              <span className="ml-1 text-sm text-green-700">节</span>
            </div>
          )}

          {/* 达标赠送 */}
          <div className="flex items-center px-4 py-3 border-t border-[#EDF2F7]">
            <label className="w-24 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              开启达标赠送
            </label>
            <div className="flex-1 flex justify-end">
              <button 
                type="button"
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-[#3182CE] focus:ring-offset-2 ${enableVolumeBonus ? 'bg-[#3182CE]' : 'bg-gray-200'}`}
                onClick={() => setEnableVolumeBonus(!enableVolumeBonus)}
              >
                <span className="sr-only">开启达标赠送</span>
                <span aria-hidden="true" className={`pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enableVolumeBonus ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>
          {enableVolumeBonus && (
            <div className="px-4 pb-3 bg-purple-50 rounded-b-xl">
              <div className="flex items-center py-2 border-b border-purple-100/50">
                <label className="w-20 text-sm font-medium text-purple-700 flex items-center gap-1">
                  达标类型
                </label>
                <input 
                  type="text" 
                  placeholder="例如：购30赠1"
                  className="flex-1 text-sm text-right bg-transparent outline-none text-purple-700" 
                  value={volumeBonusType}
                  onChange={(e) => setVolumeBonusType(e.target.value)}
                />
              </div>
              <div className="flex items-center pt-2">
                <label className="w-24 text-sm font-medium text-purple-700 flex items-center gap-1">
                  赠送课时数
                </label>
                <input 
                  type="number" 
                  placeholder="0"
                  className="flex-1 text-sm text-right outline-none text-purple-700 font-bold bg-purple-100/30 rounded px-2" 
                  min="0"
                  step="0.1"
                  value={volumeBonusHours}
                  readOnly
                />
                <span className="ml-1 text-sm text-purple-700">节</span>
              </div>
            </div>
          )}
        </div>

        {/* 支付凭证上传 */}
        <div>
          <div className="flex justify-between items-center mb-2 px-1">
            <label className="text-sm font-medium text-[#4A5568] flex items-center gap-2">
              <ImageIcon size={16} className="text-[#3182CE]" />
              支付凭证
            </label>
          </div>
          <div className="bg-white p-4 rounded-xl border border-[#EDF2F7] shadow-sm">
            <ImageUploader
              value={paymentProofUrls}
              onChange={setPaymentProofUrls}
              maxFiles={3}
              uploadMeta={{
                imageType: '购买记录',
                studentId: selectedStudentId,
                studentName: students.find(s => s.id === parseInt(selectedStudentId))?.name,
                courseId: selectedCourseId,
                courseName: courses.find(c => c.id === parseInt(selectedCourseId))?.name
              }}
              disabled={!selectedStudentId || !selectedCourseId}
              onSuccess={(urls) => console.log('支付凭证上传成功:', urls)}
              onError={(error) => toast.error(`凭证上传失败: ${error}`)}
            />
          </div>
        </div>

        {/* 备注 */}
        <div>
          <label className="text-sm font-medium text-[#4A5568] mb-2 block px-1">备注</label>
          <textarea 
            ref={remarksRef}
            className="w-full bg-white border border-[#EDF2F7] rounded-xl p-3 text-sm h-24 focus:outline-none focus:border-[#3182CE]" 
            placeholder="请输入账务备注信息..."
          />
        </div>
      </main>

      {/* 底部按钮区 - 新增模式 */}
      <div className="h-[80px] bg-white border-t border-[#EDF2F7] px-6 flex items-center justify-between flex-shrink-0 z-10">
        <Button variant="secondary" onClick={onBack} className="flex-1 mr-4" disabled={isSaving}>取消</Button>
        <Button variant="primary" className="flex-1" onClick={handleSave} disabled={isSaving}>
          {isSaving ? '保存中...' : '保存'}
        </Button>
      </div>
    </div>
  );
};

export default FinanceFormCreate;
